'use client';

import React from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/SiteFooter';

export default function PrivacyPage() {
  const sections = [
    {
      id: 'information-we-collect',
      title: 'Information We Collect',
      body: (
        <>
          <p className="mb-4">
            We collect information that you provide directly to us when you create an account, use our services,
            or communicate with us. This includes:
          </p>
          <ul className="list-disc pl-6 space-y-2 mb-4">
            <li>Account information (name, email address, company name, mobile number)</li>
            <li>Payment information (credit card details, billing address)</li>
            <li>Images and files you upload for editing</li>
            <li>Communications with our support team</li>
            <li>Usage data and preferences</li>
          </ul>
          <p>
            We also automatically collect certain information about your device and how you interact with our
            service, including IP address, browser type, operating system, and usage patterns.
          </p>
        </>
      ),
    },
    {
      id: 'how-we-use',
      title: 'How We Use Your Information',
      body: (
        <>
          <p className="mb-4">We use the information we collect to:</p>
          <ul className="list-disc pl-6 space-y-2 mb-4">
            <li>Provide, maintain, and improve our photo editing services</li>
            <li>Process your orders and deliver edited images</li>
            <li>Send you technical notices, updates, and support messages</li>
            <li>Respond to your comments, questions, and customer service requests</li>
            <li>Monitor and analyze trends, usage, and activities in connection with our services</li>
            <li>Detect, prevent, and address technical issues and fraudulent activity</li>
            <li>Personalize and improve your experience</li>
          </ul>
          <p>
            We will never sell your personal information to third parties or use your images for purposes other
            than providing our editing services without your explicit consent.
          </p>
        </>
      ),
    },
    {
      id: 'data-storage',
      title: 'Data Storage and Security',
      body: (
        <>
          <p className="mb-4">
            We take the security of your data seriously and implement appropriate technical and organizational
            measures to protect your personal information against unauthorized access, alteration, disclosure, or
            destruction.
          </p>
          <p className="mb-4">
            Your images are stored on secure servers with encryption both in transit and at rest. We use
            industry-standard SSL/TLS encryption for all data transmission. Access to your data is restricted to
            authorized personnel only.
          </p>
          <p>
            We retain your images for 30 days after delivery to allow for revisions and re-downloads. After this
            period, images are permanently deleted from our servers unless you request extended storage.
          </p>
        </>
      ),
    },
    {
      id: 'cookies',
      title: 'Cookies and Tracking',
      body: (
        <>
          <p className="mb-4">
            We use cookies and similar tracking technologies to collect information about your browsing activities
            and to remember your preferences. Cookies are small data files stored on your device.
          </p>
          <p className="mb-4">We use the following types of cookies:</p>
          <ul className="list-disc pl-6 space-y-2 mb-4">
            <li><strong className="text-white">Essential cookies:</strong> Required for the service to function properly</li>
            <li><strong className="text-white">Analytics cookies:</strong> Help us understand how users interact with our service</li>
            <li><strong className="text-white">Preference cookies:</strong> Remember your settings and preferences</li>
            <li><strong className="text-white">Marketing cookies:</strong> Used to deliver relevant advertisements</li>
          </ul>
          <p>
            You can control cookies through your browser settings. However, disabling certain cookies may limit
            your ability to use some features of our service.
          </p>
        </>
      ),
    },
    {
      id: 'third-party',
      title: 'Third-Party Services',
      body: (
        <>
          <p className="mb-4">
            We may use third-party service providers to help us operate our business and provide services to you.
            These providers have access to your personal information only to perform specific tasks on our behalf
            and are obligated not to disclose or use it for any other purpose.
          </p>
          <p className="mb-4">Third-party services we use include:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Payment processors (Stripe) for secure payment processing</li>
            <li>Cloud storage providers for secure file storage</li>
            <li>Email service providers for transactional emails</li>
            <li>Analytics providers to understand service usage</li>
          </ul>
        </>
      ),
    },
    {
      id: 'your-rights',
      title: 'Your Rights',
      body: (
        <>
          <p className="mb-4">You have certain rights regarding your personal information:</p>
          <ul className="list-disc pl-6 space-y-2 mb-4">
            <li><strong className="text-white">Access:</strong> You can request a copy of the personal information we hold about you</li>
            <li><strong className="text-white">Correction:</strong> You can update or correct your personal information at any time</li>
            <li><strong className="text-white">Deletion:</strong> You can request that we delete your personal information</li>
            <li><strong className="text-white">Portability:</strong> You can request a copy of your data in a machine-readable format</li>
            <li><strong className="text-white">Objection:</strong> You can object to certain processing of your personal information</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at{' '}
            <a href="mailto:privacy@shashio.com" className="text-purple-400 hover:text-purple-300 transition-colors">
              privacy@shashio.com
            </a>
            . We will respond to your request within 30 days.
          </p>
        </>
      ),
    },
    {
      id: 'childrens-privacy',
      title: "Children's Privacy",
      body: (
        <>
          <p className="mb-4">
            Our service is not intended for children under the age of 13. We do not knowingly collect personal
            information from children under 13. If you are a parent or guardian and believe your child has provided
            us with personal information, please contact us.
          </p>
          <p>
            If we become aware that we have collected personal information from a child under 13 without
            verification of parental consent, we will take steps to remove that information from our servers.
          </p>
        </>
      ),
    },
    {
      id: 'changes',
      title: 'Changes to Privacy Policy',
      body: (
        <>
          <p className="mb-4">
            We may update this Privacy Policy from time to time to reflect changes in our practices or for other
            operational, legal, or regulatory reasons. We will notify you of any material changes by posting the new
            Privacy Policy on this page and updating the &ldquo;Last Updated&rdquo; date.
          </p>
          <p>
            We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting
            your information.
          </p>
        </>
      ),
    },
    {
      id: 'contact',
      title: 'Contact Information',
      body: (
        <>
          <p className="mb-4">
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-6 space-y-2">
            <p><strong className="text-white">Email:</strong> <a href="mailto:admin@shashio.com" className="text-purple-400 hover:text-purple-300 transition-colors">admin@shashio.com</a></p>
            <p><strong className="text-white">Website:</strong> www.shashio.com</p>
            <p><strong className="text-white">Address:</strong> Shashio Privacy Department</p>
          </div>
        </>
      ),
    },
  ];

  const dotGrid = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;

  return (
    <div className="min-h-screen bg-[#0B0D17] text-white">
      <Header />

      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden bg-[#0B0D17]">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/3 w-[1000px] h-[1000px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
          <div className="absolute bottom-0 right-1/3 w-[800px] h-[800px] bg-blue-600/5 rounded-full blur-[140px] animate-glow-breathe-delayed" />
          <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: dotGrid }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-28 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.12] backdrop-blur-sm text-purple-200 text-xs font-medium mb-8">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span>Your Privacy Matters</span>
          </div>
          <h1 className="text-5xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-5">
            Privacy{' '}
            <span className="bg-gradient-to-r from-purple-400 via-purple-300 to-blue-400 bg-clip-text text-transparent">
              Policy.
            </span>
          </h1>
          <p className="text-lg text-gray-400">Last updated: January 1, 2026</p>
        </div>
      </section>

      {/* ===================== CONTENT ===================== */}
      <section className="relative py-16 lg:py-24 px-6 lg:px-12 border-t border-white/[0.06]">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[260px_1fr] gap-10 xl:gap-16">
          {/* Table of contents */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4">On this page</p>
              <nav className="space-y-1">
                {sections.map((s, i) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="block px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/[0.04] transition-all duration-200"
                  >
                    <span className="text-purple-400 font-semibold mr-2">{i + 1}.</span>
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Sections */}
          <div className="space-y-6">
            <div className="bg-[#13151F]/60 border border-white/[0.08] rounded-2xl p-6 lg:p-8 text-gray-400 leading-relaxed">
              This Privacy Policy explains how Shashio collects, uses, stores, and protects your information when you
              use our real estate photo editing services. By using Shashio, you agree to the practices described
              below.
            </div>

            {sections.map((section, i) => (
              <div
                key={section.id}
                id={section.id}
                className="scroll-mt-24 bg-[#13151F]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-6 lg:p-8 hover:border-purple-500/20 transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-5">
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white font-bold shadow-lg shadow-purple-500/30">
                    {i + 1}
                  </div>
                  <h2 className="text-xl lg:text-2xl font-bold text-white">{section.title}</h2>
                </div>
                <div className="text-gray-400 leading-relaxed">{section.body}</div>
              </div>
            ))}

            {/* Footer CTA */}
            <div className="relative overflow-hidden rounded-2xl border-2 border-white/[0.12] bg-[#13151F]/95 backdrop-blur-2xl p-8 lg:p-10 text-center">
              <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-purple-600/15 rounded-full blur-[120px]" />
              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-white mb-3">Questions about your privacy?</h3>
                <p className="text-gray-400 mb-6 max-w-xl mx-auto">
                  Our team is happy to walk you through how we handle and protect your data.
                </p>
                <Link href="/contact">
                  <button className="px-8 py-3.5 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-bold rounded-xl transition-all duration-300 shadow-lg shadow-purple-500/30 hover:-translate-y-0.5">
                    Contact Us
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Animations */}
      <style jsx global>{`
        @keyframes glow-breathe {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.15; transform: scale(1.05); }
        }
        @keyframes glow-breathe-delayed {
          0%, 100% { opacity: 0.05; transform: scale(1); }
          50% { opacity: 0.08; transform: scale(1.03); }
        }
        .animate-glow-breathe { animation: glow-breathe 8s ease-in-out infinite; }
        .animate-glow-breathe-delayed { animation: glow-breathe-delayed 10s ease-in-out infinite; animation-delay: 2s; }
      `}</style>
    </div>
  );
}
